/**
 * AktenOrdner.tsx — Aktenordner-System
 * 
 * Ordnerstruktur wie in Dropbox:
 * - Fotos (Fahrzeugbilder, Schadensbilder)
 * - Dokumente (Gutachten, Fahrzeugschein, etc.)
 * - Korrespondenz (E-Mails, Briefe)
 * - Stellungnahmen (Kürzungsschreiben, Prüfberichte, Antworten)
 * - Rechnungen (Ausgangsrechnungen, Eingangsrechnungen)
 * - Sonstiges
 * 
 * Features:
 * - Ordner-Navigation mit Breadcrumb
 * - Datei-Upload per Drag & Drop
 * - Bild-Vorschau (Galerie-Modus)
 * - PDF-Vorschau
 * - Datei-Umbenennung
 * - Datei-Verschieben zwischen Ordnern
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Folder, FolderOpen, Image, FileText, File, Upload, Download,
  Trash2, Eye, MoreHorizontal, ChevronRight, Home, Grid3X3,
  List, Search, Plus, X, Camera, FileCheck, Mail, Scale,
  Receipt, FolderPlus, ArrowLeft, Check, Pencil, Move,
  ZoomIn, ZoomOut, ChevronLeft, RotateCw, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface AktenDatei {
  id: string;
  name: string;
  ordner: string;
  typ: 'bild' | 'pdf' | 'dokument' | 'sonstiges';
  groesse: number;
  mime_type: string;
  url: string;
  thumbnail_url?: string;
  erstellt_am: string;
  hochgeladen_von?: string;
}

interface AktenOrdnerDef {
  id: string;
  name: string;
  icon: React.ReactNode;
  farbe: string;
  beschreibung: string;
  akzeptiert: string[]; // MIME-Types
}

interface AktenOrdnerProps {
  aktenNr: string;
  betriebId?: string;
  userId?: string | null;
  istAdmin?: boolean;
}

// ─── Standard-Ordner ──────────────────────────────────────────────────────────

const STANDARD_ORDNER: AktenOrdnerDef[] = [
  {
    id: 'fotos',
    name: 'Fotos',
    icon: <Camera className="w-5 h-5" />,
    farbe: '#3B82F6',
    beschreibung: 'Fahrzeugbilder, Schadensfotos, Lichtbilder',
    akzeptiert: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
  },
  {
    id: 'dokumente',
    name: 'Dokumente',
    icon: <FileText className="w-5 h-5" />,
    farbe: '#10B981',
    beschreibung: 'Gutachten, Fahrzeugschein, Kalkulationen',
    akzeptiert: ['application/pdf', 'image/jpeg', 'image/png'],
  },
  {
    id: 'korrespondenz',
    name: 'Korrespondenz',
    icon: <Mail className="w-5 h-5" />,
    farbe: '#8B5CF6',
    beschreibung: 'E-Mails, Briefe, Schriftverkehr',
    akzeptiert: ['application/pdf', 'image/jpeg', 'image/png', 'message/rfc822'],
  },
  {
    id: 'stellungnahmen',
    name: 'Stellungnahmen',
    icon: <Scale className="w-5 h-5" />,
    farbe: '#EF4444',
    beschreibung: 'Kürzungsschreiben, Prüfberichte, Stellungnahmen',
    akzeptiert: ['application/pdf', 'image/jpeg', 'image/png'],
  },
  {
    id: 'rechnungen',
    name: 'Rechnungen',
    icon: <Receipt className="w-5 h-5" />,
    farbe: '#F59E0B',
    beschreibung: 'Ausgangs- und Eingangsrechnungen',
    akzeptiert: ['application/pdf', 'image/jpeg', 'image/png'],
  },
  {
    id: 'sonstiges',
    name: 'Sonstiges',
    icon: <Folder className="w-5 h-5" />,
    farbe: '#6B7280',
    beschreibung: 'Weitere Dokumente und Dateien',
    akzeptiert: ['*/*'],
  },
];

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(typ: string, mimeType: string) {
  if (typ === 'bild' || mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-gray-400" />;
}

function getDateiTyp(mimeType: string): AktenDatei['typ'] {
  if (mimeType.startsWith('image/')) return 'bild';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('document') || mimeType.includes('text')) return 'dokument';
  return 'sonstiges';
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function AktenOrdner({ aktenNr, betriebId, userId, istAdmin }: AktenOrdnerProps) {
  const [aktuellerOrdner, setAktuellerOrdner] = useState<string | null>(null);
  const [dateien, setDateien] = useState<AktenDatei[]>([]);
  const [ansicht, setAnsicht] = useState<'grid' | 'list'>('grid');
  const [suchtext, setSuchtext] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedDatei, setSelectedDatei] = useState<AktenDatei | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [ordnerStats, setOrdnerStats] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ordner-Statistiken laden (Dateianzahl pro Ordner)
  useEffect(() => {
    let cancelled = false;
    async function loadOrdnerStats() {
      const stats: Record<string, number> = {};
      await Promise.all(
        STANDARD_ORDNER.map(async (ordner) => {
          try {
            const storagePath = `akten/${aktenNr}/${ordner.id}`;
            const { data } = await supabase.storage
              .from('akten-dateien')
              .list(storagePath, { limit: 200 });
            const count = (data || []).filter(f => f.name !== '.emptyFolderPlaceholder').length;
            stats[ordner.id] = count;
          } catch {
            stats[ordner.id] = 0;
          }
        })
      );
      if (!cancelled) setOrdnerStats(stats);
    }
    loadOrdnerStats();
    return () => { cancelled = true; };
  }, [aktenNr]);

  // Dateien laden
  const loadDateien = useCallback(async () => {
    if (!aktuellerOrdner) return;
    setLoading(true);
    try {
      const storagePath = `akten/${aktenNr}/${aktuellerOrdner}`;
      const { data, error } = await supabase.storage
        .from('akten-dateien')
        .list(storagePath, { limit: 200, sortBy: { column: 'created_at', order: 'desc' } });

      if (error) {
        // Bucket might not exist yet - that's OK
        console.log('Storage list:', error.message);
        setDateien([]);
        return;
      }

      const files: AktenDatei[] = (data || [])
        .filter(f => f.name !== '.emptyFolderPlaceholder')
        .map(f => {
          const { data: urlData } = supabase.storage
            .from('akten-dateien')
            .getPublicUrl(`${storagePath}/${f.name}`);
          return {
            id: f.id || f.name,
            name: f.name,
            ordner: aktuellerOrdner,
            typ: getDateiTyp(f.metadata?.mimetype || ''),
            groesse: f.metadata?.size || 0,
            mime_type: f.metadata?.mimetype || 'application/octet-stream',
            url: urlData.publicUrl,
            thumbnail_url: f.metadata?.mimetype?.startsWith('image/') ? urlData.publicUrl : undefined,
            erstellt_am: f.created_at || new Date().toISOString(),
            hochgeladen_von: (f.metadata as { uploader?: string } | undefined)?.uploader ?? undefined,
          };
        });

      setDateien(files);
    } catch (err) {
      console.error('Fehler beim Laden der Dateien:', err);
      setDateien([]);
    } finally {
      setLoading(false);
    }
  }, [aktenNr, aktuellerOrdner]);

  useEffect(() => {
    if (aktuellerOrdner) loadDateien();
  }, [aktuellerOrdner, loadDateien]);

  // Datei hochladen
  const handleUpload = useCallback(async (files: FileList | File[]) => {
    if (!aktuellerOrdner || files.length === 0) return;
    setUploading(true);
    const storagePath = `akten/${aktenNr}/${aktuellerOrdner}`;
    let successCount = 0;

    for (const file of Array.from(files)) {
      try {
        const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error } = await supabase.storage
          .from('akten-dateien')
          .upload(`${storagePath}/${fileName}`, file, {
            cacheControl: '3600',
            upsert: false,
            metadata: userId ? { uploader: userId } : undefined,
          });

        if (error) {
          console.error(`Upload-Fehler für ${file.name}:`, error);
          toast.error(`Fehler: ${file.name}`, { description: error.message });
        } else {
          successCount++;
        }
      } catch (err) {
        console.error('Upload-Fehler:', err);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Datei(en) hochgeladen`);
      loadDateien();
    }
    setUploading(false);
  }, [aktenNr, aktuellerOrdner, loadDateien, userId]);

  // Datei löschen
  const handleDelete = useCallback(async (datei: AktenDatei) => {
    if (!istAdmin && datei.hochgeladen_von && datei.hochgeladen_von !== userId) {
      toast.error('Sie können nur eigene Dateien löschen');
      return;
    }
    const storagePath = `akten/${aktenNr}/${datei.ordner}/${datei.name}`;
    const { error } = await supabase.storage
      .from('akten-dateien')
      .remove([storagePath]);

    if (error) {
      toast.error('Fehler beim Löschen', { description: error.message });
    } else {
      toast.success('Datei gelöscht');
      setDateien(prev => prev.filter(d => d.id !== datei.id));
      if (selectedDatei?.id === datei.id) {
        setSelectedDatei(null);
        setShowPreview(false);
      }
    }
  }, [aktenNr, selectedDatei, userId, istAdmin]);

  // Drag & Drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  }, [handleUpload]);

  // Gefilterte Dateien
  const gefilterteDateien = dateien.filter(d =>
    !suchtext || d.name.toLowerCase().includes(suchtext.toLowerCase())
  );

  // Ordner-Statistiken werden jetzt per useEffect geladen

  // ─── Ordner-Übersicht ─────────────────────────────────────────────────────

  if (!aktuellerOrdner) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-gray-600" />
            <h3 className="text-sm font-bold text-gray-900">Aktenordner</h3>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              {aktenNr}
            </span>
          </div>
        </div>

        {/* Ordner-Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {STANDARD_ORDNER.map(ordner => (
            <button
              key={ordner.id}
              onClick={() => setAktuellerOrdner(ordner.id)}
              className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:shadow-sm transition-all text-left group"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: ordner.farbe + '15', color: ordner.farbe }}
                >
                  {ordner.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{ordner.name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{ordner.beschreibung}</p>
                  {ordnerStats[ordner.id] !== undefined && (
                    <p className="text-[10px] font-medium text-gray-500 mt-1">
                      {ordnerStats[ordner.id]} Dateien
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Info */}
        <div className="bg-gray-50 rounded-lg p-3 text-[11px] text-gray-500">
          <p>
            Legen Sie Fotos, Dokumente und Korrespondenz in den passenden Ordnern ab.
            Die Ordnerstruktur entspricht dem Dropbox-System: <span className="font-mono font-medium">{aktenNr}</span> → Ordner → Dateien.
          </p>
        </div>
      </div>
    );
  }

  // ─── Ordner-Inhalt ────────────────────────────────────────────────────────

  const ordnerDef = STANDARD_ORDNER.find(o => o.id === aktuellerOrdner) || STANDARD_ORDNER[5];

  return (
    <div className="space-y-3">
      {/* Breadcrumb + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5 text-[12px]">
          <button
            onClick={() => { setAktuellerOrdner(null); setDateien([]); setSuchtext(''); }}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Zurück
          </button>
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <span className="font-mono text-gray-500">{aktenNr}</span>
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <span className="font-bold text-gray-800 flex items-center gap-1.5">
            <span style={{ color: ordnerDef.farbe }}>{ordnerDef.icon}</span>
            {ordnerDef.name}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Suche */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={suchtext}
              onChange={(e) => setSuchtext(e.target.value)}
              placeholder="Suchen..."
              className="pl-7 pr-3 py-1.5 text-[11px] border border-gray-200 rounded-lg w-36 focus:outline-none focus:ring-1 focus:ring-blue-300"
            />
          </div>

          {/* Ansicht */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setAnsicht('grid')}
              className={`p-1.5 ${ansicht === 'grid' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Grid3X3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setAnsicht('list')}
              className={`p-1.5 ${ansicht === 'list' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Upload */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[11px] font-medium hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Hochladen
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`min-h-[300px] rounded-xl border-2 border-dashed transition-all ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-transparent'
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : gefilterteDateien.length === 0 ? (
          /* Leerer Ordner */
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
              style={{ backgroundColor: ordnerDef.farbe + '10', color: ordnerDef.farbe }}
            >
              <Upload className="w-7 h-7" />
            </div>
            <p className="text-sm font-medium text-gray-600">Keine Dateien in diesem Ordner</p>
            <p className="text-[11px] text-gray-400 mt-1">
              Dateien hierher ziehen oder auf "Hochladen" klicken
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-[11px] font-medium text-gray-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Dateien hinzufügen
            </button>
          </div>
        ) : ansicht === 'grid' ? (
          /* Grid-Ansicht */
          <div className={`grid gap-3 ${
            aktuellerOrdner === 'fotos' 
              ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5' 
              : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
          }`}>
            {gefilterteDateien.map(datei => (
              <div
                key={datei.id}
                onClick={() => { setSelectedDatei(datei); setShowPreview(true); }}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer group"
              >
                {/* Vorschau */}
                <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden relative">
                  {datei.typ === 'bild' ? (
                    <img
                      src={datei.url}
                      alt={datei.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : datei.typ === 'pdf' ? (
                    <div className="flex flex-col items-center gap-1">
                      <FileText className="w-8 h-8 text-red-400" />
                      <span className="text-[9px] text-gray-400 font-medium">PDF</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <File className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  {/* Hover-Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Eye className="w-5 h-5 text-white drop-shadow-md" />
                  </div>
                </div>
                {/* Info */}
                <div className="p-2">
                  {renamingId === datei.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="flex-1 text-[10px] border border-blue-300 rounded px-1 py-0.5 focus:outline-none"
                        autoFocus
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && renameValue.trim()) {
                            try {
                              const oldPath = `akten/${aktenNr}/${aktuellerOrdner}/${datei.name}`;
                              const newPath = `akten/${aktenNr}/${aktuellerOrdner}/${renameValue.trim()}`;
                              // Supabase Storage hat kein rename — copy + delete
                              const { data: fileData } = await supabase.storage.from('akten-dateien').download(oldPath);
                              if (fileData) {
                                await supabase.storage.from('akten-dateien').upload(newPath, fileData, { upsert: true, metadata: userId ? { uploader: userId } : undefined });
                                await supabase.storage.from('akten-dateien').remove([oldPath]);
                                toast.success(`Umbenannt: ${renameValue.trim()}`);
                                loadDateien();
                              } else {
                                toast.error('Datei konnte nicht gelesen werden');
                              }
                            } catch (err) {
                              toast.error('Fehler beim Umbenennen');
                              console.error('Rename error:', err);
                            }
                            setRenamingId(null);
                          }
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                      />
                      <button onClick={() => setRenamingId(null)} className="p-0.5">
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] font-medium text-gray-700 truncate" title={datei.name}>
                      {datei.name}
                    </p>
                  )}
                  <p className="text-[9px] text-gray-400 mt-0.5">{formatFileSize(datei.groesse)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Listen-Ansicht */
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-medium text-gray-500">Name</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-500 hidden sm:table-cell">Typ</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-500">Größe</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-500 hidden sm:table-cell">Datum</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {gefilterteDateien.map(datei => (
                  <tr
                    key={datei.id}
                    onClick={() => { setSelectedDatei(datei); setShowPreview(true); }}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {getFileIcon(datei.typ, datei.mime_type)}
                        <span className="font-medium text-gray-800 truncate max-w-[200px]">{datei.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">
                      {datei.mime_type.split('/').pop()?.toUpperCase()}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 font-mono">
                      {formatFileSize(datei.groesse)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 hidden sm:table-cell">
                      {new Date(datei.erstellt_am).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-2 py-2">
                      {(istAdmin || !datei.hochgeladen_von || datei.hochgeladen_von === userId) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(datei);
                          }}
                          className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload-Indikator */}
      {uploading && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          <span className="text-[11px] text-blue-700 font-medium">Dateien werden hochgeladen...</span>
        </div>
      )}

      {/* Bild-/PDF-Vorschau Modal */}
      {showPreview && selectedDatei && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2 min-w-0">
                {getFileIcon(selectedDatei.typ, selectedDatei.mime_type)}
                <span className="text-sm font-medium text-gray-800 truncate">{selectedDatei.name}</span>
                <span className="text-[10px] text-gray-400">{formatFileSize(selectedDatei.groesse)}</span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={selectedDatei.url}
                  download={selectedDatei.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Herunterladen"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => {
                    setRenamingId(selectedDatei.id);
                    setRenameValue(selectedDatei.name);
                    setShowPreview(false);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Umbenennen"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {(istAdmin || !selectedDatei.hochgeladen_von || selectedDatei.hochgeladen_von === userId) && (
                  <button
                    onClick={() => {
                      handleDelete(selectedDatei);
                      setShowPreview(false);
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Preview Content */}
            <div className="flex items-center justify-center bg-gray-100 overflow-auto" style={{ maxHeight: 'calc(90vh - 60px)' }}>
              {selectedDatei.typ === 'bild' ? (
                <img
                  src={selectedDatei.url}
                  alt={selectedDatei.name}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : selectedDatei.typ === 'pdf' ? (
                <iframe
                  src={selectedDatei.url}
                  className="w-full h-[80vh]"
                  title={selectedDatei.name}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-48 gap-3">
                  <File className="w-12 h-12 text-gray-300" />
                  <p className="text-sm text-gray-500">Vorschau nicht verfügbar</p>
                  <a
                    href={selectedDatei.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-[11px] font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Herunterladen
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
